// ============================================================================
// Admin → Feedback triage view (/admin/feedback)
//
// Lists all app_feedback rows. Superuser-gated. RLS already enforces this
// at the DB level (users see only their own; superuser sees all), so an
// AM landing here will see only their own entries — harmless, but the
// guard below short-circuits with a friendlier message.
//
// Filters: status (all / new / in_progress / etc), type (all / bug / etc).
// Click a row to expand inline (description, captured context, user agent).
// Status transitions update app_feedback directly (RLS lets superuser
// UPDATE) — EXCEPT 'completed'.
//
// CW-R (2026-08-14) — closing an item requires EVIDENCE, and this screen had
// no way to supply it. CHECK `app_feedback_completed_requires_evidence`
// (7/29) demands a non-blank `resolution`; BEFORE trigger
// `trg_app_feedback_completion_guard` (8/14) raises P0001 without one. This
// screen only ever sent {status, resolved_at}, so EVERY in-app closure
// failed, silently, for six weeks. All 77 resolutions on the table were
// written out-of-band by SQL — audit_log shows changed_by_auth_uid NULL and
// role 'none' on all 77, i.e. not one came from a signed-in user. The
// 'completed' button therefore opens ResolveDialog instead of writing.
//
// The dialog sends {status:'completed', resolution} and NOTHING else.
// resolved_at and resolved_by are the trigger's to stamp: a browser that
// supplies resolved_by is a browser deciding who signed off (AFB-0078).
//
// The resolution is not merely constraint food. notify_src_app_feedback uses
// it verbatim as the BODY of the closure notification the original submitter
// receives.
//
// W-01 (2026-08-18) — and the TITLE is no longer always "Your feedback
// shipped". That title fired on every transition to 'completed' regardless of
// how the item was closed, so superseding AFB-0080 told Jose Azocar his
// feedback had shipped when nothing had. The title now branches on
// duplicate_of / wont_fix / duplicate, and all three terminal statuses close
// their notifications and DM the reporter — previously only 'completed' did
// either, which is why choosing wont_fix left the triage notifications active
// forever and told the reporter nothing at all.
// ============================================================================

import { isSuperuser } from '../../shared/role-gates';
import { useEffect, useState } from 'react';
import { Bug, Lightbulb, MessageCircle, RefreshCw, Sparkles, X } from 'lucide-react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
} from '../../shared/components';
import { describeError, errorMessage } from '../../shared/errors';
import type { DescribedError } from '../../shared/errors';
import { overlayClickClose } from '../../shared/overlay-close';
import { saveFeedback, type FeedbackSave } from './feedback-save';

type FeedbackRow = {
  feedback_id: string;
  feedback_type: string;
  source: string;
  status: string;
  title: string;
  description: string;
  module: string | null;
  url_path: string | null;
  chat_excerpt: string | null;
  user_agent: string | null;
  submitted_by: string | null;
  submitted_by_employee_id: string | null;
  submitted_by_display_name: string | null;
  created_at: string;
  resolved_at: string | null;
  resolution: string | null;
  admin_notes: string | null;
  // What supersedes this item, when it was closed as a duplicate. Read by the
  // list (so the link is visible, not just implied) and written by
  // updateStatus; notify_src_app_feedback titles the reporter's DM off it.
  duplicate_of: string | null;
};

type StatusValue = 'new' | 'triaged' | 'in_progress' | 'completed' | 'wont_fix' | 'duplicate';
const STATUS_OPTIONS: StatusValue[] = [
  'new',
  'triaged',
  'in_progress',
  'completed',
  'wont_fix',
  'duplicate',
];

// Every status the "Set status" control may write DIRECTLY. 'completed' is
// deliberately absent: it is gated behind ResolveDialog, and excluding it from
// the type makes the direct-write path unable to close an item at COMPILE
// TIME rather than by convention — the next person to add a caller cannot
// reintroduce the evidence-less closure without tsc saying so.
type DirectStatusValue = Exclude<StatusValue, 'completed'>;

/**
 * A submitter, resolved for display. `app_feedback` stores the reporter three
 * ways — a `submitted_by` uuid, a `submitted_by_employee_id`, and a
 * denormalised `submitted_by_display_name` — and the screen was rendering the
 * uuid, which is the one a person cannot act on.
 *
 * `employee_id` is the single source (the `account_manager_name` lesson: a
 * stored name copy drifts from the id and there is no way to tell which is
 * right). The stored display name is kept only as a fallback and as the
 * annotation note below.
 */
type Person = { name: string; title: string | null; email: string | null };

const TYPE_OPTIONS = ['bug', 'feature_request', 'improvement', 'question', 'other'];

export default function AdminFeedback() {
  const { profile } = useAuth();
  const [rows, setRows] = useState<FeedbackRow[]>([]);
  const [people, setPeople] = useState<Record<string, Person>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [errorContext, setErrorContext] = useState<'load' | 'update'>('load');
  // The row whose closure is being written up. Non-null = ResolveDialog open.
  const [resolving, setResolving] = useState<FeedbackRow | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase
        .from('app_feedback')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (fetchErr) throw fetchErr;
      setRows(data ?? []);
      await loadPeople(data ?? []);
    } catch (e) {
      console.error('[admin/feedback] load failed', e);
      setErrorContext('load');
      setError(errorMessage(e, 'Failed to load feedback.'));
    } finally {
      setLoading(false);
    }
  }

  /**
   * Resolve the submitters. Flat fetch + IN lookup + client join — the house
   * pattern, not a PostgREST embed.
   *
   * TWO views on purpose, and the split is the point:
   *
   *   v_employee_display  name + title. No role predicate, granted to
   *                       `authenticated`. THE NAME COMES FROM HERE.
   *   v_employees_am      email_work. Its WHERE is
   *                       `is_am_role() OR role IN (hr,finance,executive) OR
   *                       is_superuser()`, so it returns ZERO ROWS — not an
   *                       error — for a caller it doesn't cover.
   *
   * Hanging the name off the gated view would make an unresolved JWT look
   * exactly like "this person doesn't exist": a truthful, empty, wrong answer
   * that no test catches. The name never depends on a role predicate; only the
   * contact line does, and that one degrades to simply not being shown.
   *
   * Never throws into the page. A resolver that fails costs a display name; it
   * must not cost the triage queue.
   */
  async function loadPeople(fromRows: FeedbackRow[]) {
    const eids = [
      ...new Set(fromRows.map((r) => r.submitted_by_employee_id).filter(Boolean)),
    ] as string[];
    if (eids.length === 0) return;

    try {
      const [named, contactable] = await Promise.all([
        supabase
          .from('v_employee_display')
          .select('employee_id, employee_full_name, employee_title')
          .in('employee_id', eids),
        supabase.from('v_employees_am').select('employee_id, email_work').in('employee_id', eids),
      ]);
      if (named.error) throw named.error;

      const emails = new Map<string, string | null>(
        (contactable.data ?? []).map((e: { employee_id: string; email_work: string | null }) => [
          e.employee_id,
          e.email_work,
        ]),
      );

      const next: Record<string, Person> = {};
      for (const e of (named.data ?? []) as Array<{
        employee_id: string;
        employee_full_name: string | null;
        employee_title: string | null;
      }>) {
        if (!e.employee_full_name) continue;
        next[e.employee_id] = {
          name: e.employee_full_name,
          title: e.employee_title,
          email: emails.get(e.employee_id) ?? null,
        };
      }
      setPeople(next);
    } catch (e) {
      // Deliberately swallowed to a log: the card falls back to the stored
      // display name, then to the EID. Feedback triage still works.
      console.error('[admin/feedback] submitter lookup failed', e);
    }
  }

  useEffect(() => {
    load();
  }, []);

  // Guard. Superuser-only at the UI layer; RLS enforces at the DB layer.
  if (profile && !isSuperuser(profile)) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <PageHeader title="Feedback" />
        <EmptyState
          title="Superusers only"
          body="The feedback triage view is gated to superusers. If you submitted feedback, you'll see your own entries reflected in the database — but you don't have access to triage everyone else's."
        />
      </div>
    );
  }

  // Direct write path. 'completed' cannot reach here — see DirectStatusValue.
  //
  // It sends {status} — plus duplicate_of for a supersede — AND NOTHING ELSE.
  // Every closure stamp belongs to the database: app_feedback_completion_guard
  // (rewritten 2026-08-18, W-02) stamps resolved_at + resolved_by on the way
  // INTO any of the three terminal statuses — completed, wont_fix, duplicate —
  // and clears resolved_at, resolved_by AND resolution on the way out of one.
  //
  // What used to live here: a client-side resolved_at stamp for wont_fix /
  // duplicate, and an explicit `resolved_by: null` when undoing a closure,
  // because the guard keyed on 'completed' only and stamped resolved_by just
  // `if NEW.resolved_by is null` — so a reopened item kept its old signature and
  // credited the FIRST closer on every later closure. That retraction now
  // happens in the trigger, where it covers every write path instead of this
  // one screen. One screen defending a table-wide invariant is a grep presented
  // as a guarantee.
  //
  // The server's row is taken verbatim on success: the optimistic row cannot
  // know the timestamps the transaction produced, and guessing them on screen
  // is how AFB-0080 came to display a completion time 18 minutes before the
  // update that completed it.
  async function updateStatus(id: string, status: DirectStatusValue) {
    const before = rows.find((r) => r.feedback_id === id);

    // A supersede has to name what supersedes it. notify_src_app_feedback
    // titles the reporter's DM "Superseded by AFB-####" off duplicate_of, and
    // until now this screen had no way to supply it — so every in-app duplicate
    // closure produced "no superseding item was recorded". Same shape as the
    // CW-R resolution gap: the database could express the fact, the UI could
    // not. Blank is still allowed; it just means nobody said which.
    let duplicateOf: string | null | undefined;
    if (status === 'duplicate') {
      const answer = window.prompt(
        'Which item supersedes this one? Enter its ID (e.g. AFB-0081), or leave ' +
          'blank if there isn’t one.\n\nThe reporter is told "Superseded by ' +
          '<ID>", so this is what they will see.',
        '',
      );
      if (answer === null) return; // cancelled the prompt — no write
      const trimmed = answer.trim().toUpperCase();
      if (trimmed !== '') {
        if (!/^AFB-\d{4}$/.test(trimmed)) {
          setErrorContext('update');
          setError(`"${answer.trim()}" isn't a feedback ID. They look like AFB-0081.`);
          return;
        }
        if (trimmed === id) {
          setErrorContext('update');
          setError('An item cannot supersede itself.');
          return;
        }
        // Checked against what this screen has loaded (500 most recent). A real
        // ID beyond that window would fail the FK server-side and surface
        // through describeError, so this is a friendlier first pass, not the
        // guarantee.
        if (!rows.some((r) => r.feedback_id === trimmed)) {
          setErrorContext('update');
          setError(`${trimmed} isn't in this list. Check the ID and try again.`);
          return;
        }
        duplicateOf = trimmed;
      }
    }

    setRows((cur) => cur.map((r) => (r.feedback_id === id ? { ...r, status } : r)));

    const payload: Record<string, unknown> = { status };
    if (duplicateOf !== undefined) payload.duplicate_of = duplicateOf;

    // The shared write (feedback-save.ts): Admin → Feedback and the command
    // center's Feedback tab send exactly the same thing.
    const { data, error: updateErr } = await saveFeedback(id, payload as FeedbackSave);
    if (updateErr) {
      // Roll back THIS row only. Restoring a whole-array snapshot would also
      // revert any optimistic change made by a request that started later.
      if (before) setRows((cur) => cur.map((r) => (r.feedback_id === id ? before : r)));
      setErrorContext('update');
      setError(errorMessage(updateErr, "Couldn't update feedback.", 'admin/feedback'));
      return;
    }
    if (data) setRows((cur) => cur.map((r) => (r.feedback_id === id ? (data as FeedbackRow) : r)));
  }

  // The ONE write that closes an item.
  //
  // Returns null on success, or the described failure for the DIALOG to
  // render. It deliberately never calls setError(): a page-level ErrorState
  // replaces this entire screen, which would unmount the dialog and take the
  // user's typed resolution with it — the CW-O trap, where the surface
  // holding the message died before anyone could read it.
  async function completeItem(
    id: string,
    resolution: string,
    adminNotes: string | null | undefined,
  ): Promise<DescribedError | null> {
    const before = rows.find((r) => r.feedback_id === id);
    setRows((cur) =>
      cur.map((r) =>
        r.feedback_id === id
          ? {
              ...r,
              status: 'completed',
              resolution,
              ...(adminNotes === undefined ? {} : { admin_notes: adminNotes }),
            }
          : r,
      ),
    );

    // resolved_at / resolved_by are absent ON PURPOSE — the guard trigger
    // stamps both, and it is the DB's business who signed off, not ours.
    const payload: Record<string, unknown> = { status: 'completed', resolution };
    if (adminNotes !== undefined) payload.admin_notes = adminNotes;

    // The shared write (feedback-save.ts): Admin → Feedback and the command
    // center's Feedback tab send exactly the same thing.
    const { data, error: updateErr } = await saveFeedback(id, payload as FeedbackSave);

    if (updateErr) {
      // Roll back THIS row only — see updateStatus.
      if (before) setRows((cur) => cur.map((r) => (r.feedback_id === id ? before : r)));
      const described = describeError(updateErr, "Couldn't complete this item.");
      console.error('[admin/feedback] complete failed', {
        code: described.code,
        constraint: described.constraint,
        raw: updateErr,
      });
      return described;
    }

    // Take the server's row verbatim: resolved_at and resolved_by were
    // stamped inside the transaction and the optimistic row cannot know them.
    if (data) setRows((cur) => cur.map((r) => (r.feedback_id === id ? (data as FeedbackRow) : r)));
    return null;
  }

  const filtered = rows.filter((r) => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (typeFilter !== 'all' && r.feedback_type !== typeFilter) return false;
    return true;
  });

  const counts = {
    new: rows.filter((r) => r.status === 'new').length,
    in_progress: rows.filter((r) => r.status === 'in_progress').length,
    completed: rows.filter((r) => r.status === 'completed').length,
    total: rows.length,
  };

  // The dialog is rendered by ALL THREE returns below, on purpose.
  //
  // TCSD-0044 says a modal reports its own failures — but that is only half the
  // protection. The other half is that nothing ELSE may unmount it either, and
  // both early returns below replace the entire screen. Refresh while the dialog
  // is open (the button is still reachable by keyboard behind the overlay) flips
  // `loading`, and a background status write that fails flips `error` — either
  // one would have destroyed the resolution the user had typed but not saved.
  // Hoisting the node means the dialog outlives both.
  const dialog = resolving ? (
    <ResolveDialog
      row={resolving}
      onClose={() => setResolving(null)}
      onSave={(resolution, adminNotes) =>
        completeItem(resolving.feedback_id, resolution, adminNotes)
      }
    />
  ) : null;

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <PageHeader title="Feedback" />
        <div className="mt-4 space-y-2">
          <Skeleton width="100%" height={60} block />
          <Skeleton width="100%" height={60} block />
          <Skeleton width="100%" height={60} block />
        </div>
        {dialog}
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <PageHeader title="Feedback" />
        <ErrorState
          title={errorContext === 'update' ? "Couldn't update feedback" : "Couldn't load feedback"}
          body={error}
          onRetry={load}
        />
        {dialog}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <PageHeader
        title="Feedback"
        meta={`${counts.new} new · ${counts.in_progress} in progress · ${counts.completed} completed · ${counts.total} total`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={load}
            icon={<RefreshCw className="h-3.5 w-3.5" />}
          >
            Refresh
          </Button>
        }
      />

      {/* Filters */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
            Status
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded border border-tcs-border bg-tcs-field px-2 py-1 text-xs text-tcs-text"
          >
            <option value="all">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
            Type
          </span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded border border-tcs-border bg-tcs-field px-2 py-1 text-xs text-tcs-text"
          >
            <option value="all">All</option>
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {t.replace('_', ' ')}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* List */}
      <div className="mt-4 space-y-2">
        {filtered.length === 0 ? (
          <EmptyState
            title="Nothing here"
            body={
              statusFilter !== 'all' || typeFilter !== 'all'
                ? 'No entries match those filters.'
                : 'No feedback submitted yet. The button is live on every screen — once testers start clicking it, items will land here.'
            }
          />
        ) : (
          filtered.map((row) => (
            <FeedbackRowCard
              key={row.feedback_id}
              row={row}
              expanded={expanded === row.feedback_id}
              onToggle={() =>
                setExpanded(expanded === row.feedback_id ? null : row.feedback_id)
              }
              person={
                row.submitted_by_employee_id
                  ? people[row.submitted_by_employee_id] ?? null
                  : null
              }
              onUpdateStatus={(status) => updateStatus(row.feedback_id, status)}
              onRequestComplete={() => setResolving(row)}
            />
          ))
        )}
      </div>

      {dialog}
    </div>
  );
}

function FeedbackRowCard({
  row,
  expanded,
  onToggle,
  person,
  onUpdateStatus,
  onRequestComplete,
}: {
  row: FeedbackRow;
  expanded: boolean;
  onToggle: () => void;
  person: Person | null;
  onUpdateStatus: (status: DirectStatusValue) => Promise<void>;
  onRequestComplete: () => void;
}) {
  const typeIcon =
    row.feedback_type === 'bug' ? (
      <Bug className="h-4 w-4 text-st-danger-fg" />
    ) : row.feedback_type === 'feature_request' ? (
      <Sparkles className="h-4 w-4 text-purple-600" />
    ) : row.feedback_type === 'improvement' ? (
      <Lightbulb className="h-4 w-4 text-st-warn-fg" />
    ) : (
      <MessageCircle className="h-4 w-4 text-tcs-text-secondary" />
    );

  // Who reported this, in the order a person can act on. The `submitted_by`
  // uuid is deliberately absent from every branch — it was what the card used
  // to render, and it is the one value nobody can follow up with.
  const submittedByLabel =
    person?.name ?? row.submitted_by_display_name ?? row.submitted_by_employee_id ?? null;

  // One row (AFB-0045) carries a hand-written `submitted_by_display_name` —
  // "Jose Azocar (originator) & Troy Dittemore (co-reporter)" — that no id can
  // reproduce. Resolving strictly from the employee id would silently delete
  // what a person deliberately wrote there, so when the stored string says
  // something the resolver didn't, it is shown as well. The resolved name stays
  // the authority; the note is evidence, not a second source of truth.
  const annotation =
    person && row.submitted_by_display_name && row.submitted_by_display_name !== person.name
      ? row.submitted_by_display_name
      : null;

  const statusToneClasses =
    row.status === 'new'
      ? 'bg-st-warn-bg text-st-warn-fg-strong border-st-warn-border'
      : row.status === 'in_progress'
        ? 'bg-st-warn-bg text-st-warn-fg-strong border-st-warn-border'
        : row.status === 'completed'
          ? 'bg-st-ok-bg text-st-ok-fg-strong border-st-ok-border'
          : 'bg-tcs-border text-tcs-text-secondary border-tcs-border';

  return (
    <div className="rounded-xl border border-tcs-border bg-tcs-bg">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-3 p-3 text-left hover:bg-tcs-bg-muted"
      >
        {typeIcon}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-tcs-text">
              {row.title}
            </span>
            <span
              className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${statusToneClasses}`}
            >
              {row.status.replace('_', ' ')}
            </span>
            {/* The supersede link, said out loud. "duplicate" on its own asks
                the reader to go and find out what it duplicates; the reporter's
                DM already names it, so the triage list should too. */}
            {row.duplicate_of && (
              <span className="shrink-0 rounded bg-tcs-surface px-1.5 py-0.5 font-mono text-[10px] text-tcs-text-secondary">
                → {row.duplicate_of}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-tcs-text-muted">
            <span className="font-mono">{row.feedback_id}</span>
            <span>·</span>
            <span>{row.feedback_type.replace('_', ' ')}</span>
            <span>·</span>
            <span>{new Date(row.created_at).toLocaleDateString()}</span>
            {row.url_path && (
              <>
                <span>·</span>
                <code className="truncate font-mono">{row.url_path}</code>
              </>
            )}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-tcs-border p-3 text-sm">
          <p className="whitespace-pre-wrap text-tcs-text">{row.description}</p>

          {row.chat_excerpt && (
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                Chat excerpt
              </p>
              <p className="mt-1 whitespace-pre-wrap rounded bg-tcs-bg-muted p-2 text-xs text-tcs-text-secondary">
                {row.chat_excerpt}
              </p>
            </div>
          )}

          <div className="mt-3 grid grid-cols-2 gap-3 text-[11px] text-tcs-text-muted sm:grid-cols-3">
            <div>
              <div className="font-semibold uppercase tracking-[0.08em]">Source</div>
              <div className="mt-0.5">{row.source.replace('_', ' ')}</div>
            </div>
            {row.module && (
              <div>
                <div className="font-semibold uppercase tracking-[0.08em]">Module</div>
                <div className="mt-0.5">{row.module}</div>
              </div>
            )}
            {submittedByLabel && (
              <div className="min-w-0">
                <div className="font-semibold uppercase tracking-[0.08em]">By</div>
                <div className="mt-0.5 truncate text-tcs-text">{submittedByLabel}</div>
                {person?.title && <div className="truncate">{person.title}</div>}
                {person?.email && (
                  <a
                    href={`mailto:${person.email}?subject=${encodeURIComponent(
                      `${row.feedback_id} — ${row.title}`,
                    )}`}
                    className="mt-0.5 block truncate text-tcs-accent-ink underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {person.email}
                  </a>
                )}
                {annotation && (
                  <div className="mt-0.5 italic">Recorded as: {annotation}</div>
                )}
              </div>
            )}
          </div>

          {row.user_agent && (
            <details className="mt-3 text-[11px] text-tcs-text-muted">
              <summary className="cursor-pointer font-semibold uppercase tracking-[0.08em]">
                User agent
              </summary>
              <code className="mt-1 block whitespace-pre-wrap rounded bg-tcs-bg-muted p-2 font-mono">
                {row.user_agent}
              </code>
            </details>
          )}

          {/* Evidence of closure. Rendered because it is the thing the guard
              trigger demands and the thing the submitter is notified with —
              if it is not on screen, nobody can tell whether it landed. */}
          {(row.resolution || row.admin_notes) && (
            <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg-muted p-2">
              {row.resolution && (
                <>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                    Resolution
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-tcs-text">
                    {row.resolution}
                  </p>
                </>
              )}
              {row.admin_notes && (
                <>
                  <p className="mt-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
                    Admin notes
                  </p>
                  <p className="mt-1 whitespace-pre-wrap text-xs text-tcs-text-secondary">
                    {row.admin_notes}
                  </p>
                </>
              )}
              {row.resolved_at && (
                <p className="mt-2 text-[11px] text-tcs-text-muted">
                  Resolved {new Date(row.resolved_at).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Status transitions */}
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
              Set status
            </span>
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                // 'completed' NEVER writes from here — it collects evidence
                // first. Every other status is a direct write.
                //
                // NO `as DirectStatusValue` CAST. TypeScript narrows `s` on the
                // equality check, so the false branch is already the excluded
                // union — and that narrowing is the whole guarantee. A cast here
                // would launder `StatusValue` straight back into the direct write
                // path and tsc would stay silent, which is exactly the failure
                // this type exists to make impossible.
                onClick={() => (s === 'completed' ? onRequestComplete() : onUpdateStatus(s))}
                disabled={row.status === s}
                className={`rounded border px-2 py-0.5 text-[11px] ${
                  row.status === s
                    ? 'border-tcs-accent bg-tcs-accent/10 text-tcs-accent-ink'
                    : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:border-tcs-text-secondary'
                }`}
              >
                {s.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ResolveDialog — the missing input (CW-R).
//
// One required field. The database will refuse a blank resolution, so this
// refuses first and says why in the user's language; but it still renders the
// DATABASE's own P0001 message inline if one ever gets through, because a
// validation rule that lives in two places will eventually disagree with
// itself, and when it does the DB is right.
//
// Every failure stays ON the dialog. Handing it to the page-level ErrorState
// would unmount this component and discard whatever the person had typed.
//
// ⚠️ Do NOT write a PR reference as a hash + number in any string here.
// check-raw-palette.mjs counts a hash followed by 3-8 hex digits as a colour
// literal, and 2/9/1 are all hex — so a placeholder reading "PR" plus that
// reference fails the ratchet on this file with "literal: 0 to 1".
// ============================================================================
function ResolveDialog({
  row,
  onClose,
  onSave,
}: {
  row: FeedbackRow;
  onClose: () => void;
  onSave: (
    resolution: string,
    adminNotes: string | null | undefined,
  ) => Promise<DescribedError | null>;
}) {
  const [resolution, setResolution] = useState(row.resolution ?? '');
  const [adminNotes, setAdminNotes] = useState(row.admin_notes ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<DescribedError | null>(null);
  const [touched, setTouched] = useState(false);

  const trimmed = resolution.trim();
  const blank = trimmed.length === 0;

  // P0001 is the completion guard talking. That is a validation message about
  // the field on this dialog — not a page-level failure.
  const dbValidation = failure?.code === 'P0001' ? failure : null;
  const otherFailure = failure && !dbValidation ? failure : null;
  const fieldError =
    blank && touched
      ? 'Say what resolved this — one line is enough.'
      : dbValidation?.message ?? null;

  function dismiss() {
    if (!submitting) onClose();
  }

  // Escape closes, but never mid-write: dismissing a save already in flight
  // would hide an outcome the user has no other way to see.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  async function handleSave() {
    if (blank) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setFailure(null);

    // Only send admin_notes when it actually changed. Prefilled-and-untouched
    // would rewrite its own value for no reason, and a blank one would wipe
    // notes somebody else wrote.
    const notes = adminNotes.trim();
    const existing = (row.admin_notes ?? '').trim();
    const notesArg = notes === existing ? undefined : notes || null;

    const err = await onSave(trimmed, notesArg);
    setSubmitting(false);
    if (err) {
      setFailure(err);
      return;
    }
    onClose();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Resolve ${row.feedback_id}`}
      {...overlayClickClose(dismiss)}
    >
      <div
        // max-h + overflow-y-auto matches every other modal in the repo, and it
        // is load-bearing here: both textareas are resize-y, so without a cap
        // the card grows past a fixed, non-scrolling overlay and pushes Save
        // and Cancel off-screen with no way to scroll to them.
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-tcs-bg p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-tcs-text">Complete this item</h2>
            <p className="truncate text-xs text-tcs-text-muted">
              <span className="font-mono">{row.feedback_id}</span> · {row.title}
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={dismiss}
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <label className="mt-2 block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            What resolved this? <span className="text-rag-red">*</span>
          </span>
          <textarea
            rows={4}
            autoFocus
            value={resolution}
            // Editing invalidates the last verdict. Without this the DB's
            // P0001 — "Resolution required…" — survives the edit and reappears
            // under a completely different sentence the DB has never seen,
            // asserting a rejection that never happened.
            onChange={(e) => {
              setResolution(e.target.value);
              if (failure) setFailure(null);
            }}
            onBlur={() => setTouched(true)}
            placeholder="e.g. Fixed in PR 291 — the picker now excludes terminated facilities."
            className="w-full resize-y rounded-md border border-tcs-border-strong bg-tcs-bg p-3 text-sm focus:border-tcs-accent focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
          />
          <span className="mt-1 block text-[11px] text-tcs-text-muted">
            Required. This is the message the person who reported it receives.
          </span>
        </label>

        {fieldError && (
          <div className="mt-2 rounded-md border border-rag-red/40 bg-rag-red/10 p-2 text-xs text-rag-red">
            {fieldError}
          </div>
        )}

        <label className="mt-3 block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
            Admin notes <span className="ml-1 text-tcs-text-muted">(optional)</span>
          </span>
          <textarea
            rows={2}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder="Internal only — not sent to the reporter."
            className="w-full resize-y rounded-md border border-tcs-border-strong bg-tcs-bg p-3 text-sm focus:border-tcs-accent focus:outline-none focus:ring-2 focus:ring-tcs-accent-bg"
          />
        </label>

        {otherFailure && (
          <div className="mt-3 rounded-md border border-rag-red/40 bg-rag-red/10 p-2 text-xs text-rag-red">
            <p>{otherFailure.message}</p>
            {otherFailure.detail && (
              <p className="mt-1 text-tcs-text-muted">{otherFailure.detail}</p>
            )}
            <p className="mt-1 text-tcs-text-muted">
              Nothing was saved — your text is still here, so you can retry.
            </p>
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={dismiss} disabled={submitting}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={submitting || blank}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}
